import React, { useRef, useSyncExternalStore } from "react";
import {
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
  Paper,
} from "@mui/material";
import { useAppTranslate } from "src/hooks/useAppTranslate";
import { FileWithPreview } from "@contexts/index";
import PhotoUpload from "@components/photo-upload";
import PhotoList from "@components/photo-preview";
import ActionButton from "@components/atoms/action-button";
import { NestJSError } from "../types";
import { localizeServerMessage } from "src/utils/server-validation";
import { detectCurrentInAppBrowser } from "src/utils/in-app-browser";
import { useScreenWakeLock } from "src/hooks/use-screen-wake-lock";

interface UploadSectionProps {
  files: FileWithPreview[];
  disableSubmit: boolean;
  onUpload: (acceptedFiles: File[], thumbnails: string[]) => void | Promise<void>;
  onReorder: (
    dragIndex: number,
    directionOrHoverIndex: number | "up" | "down"
  ) => void;
  onCreateOrderAndUpload: () => void;
  uploadError: NestJSError | null;
  originalUploadError: NestJSError | null;
  uploading: boolean;
  originalUploading: boolean;
  uploadProgress: number;
  originalUploadProgress: number;
  uploadingBuffer: number;
  originalUploadingBuffer: number;
  setDisableSubmit: (disable: boolean) => void;
  /** Optional content rendered inside the fixed "uploading" overlay, below the
   *  progress bar (e.g. the "save time at checkout" prefill panel). Placed here
   *  because the overlay is position:fixed / zIndex:9999 and would otherwise
   *  cover anything rendered in normal flow. */
  overlaySlot?: React.ReactNode;
}

const noSubscription = () => () => {};

/** The upload error box, scrolled into view when it appears. */
export const UPLOAD_ERROR_ID = "order-upload-error";

const UploadSection: React.FC<UploadSectionProps> = ({
  files,
  disableSubmit,
  onUpload,
  onReorder,
  onCreateOrderAndUpload,
  uploadError,
  originalUploadError,
  uploading,
  originalUploading,
  uploadProgress,
  originalUploadProgress,
  uploadingBuffer: _uploadingBuffer,
  originalUploadingBuffer: _originalUploadingBuffer,
  setDisableSubmit,
  overlaySlot,
}) => {
  const translate = useAppTranslate();
  const parentRef = useRef<HTMLDivElement>(null);

  const isUploadingAny = uploading || originalUploading;

  // Keep the screen on while uploading, in every browser: a phone that locks
  // itself backgrounds the page, and a backgrounded page loses its network.
  useScreenWakeLock(isUploadingAny);

  // The server has no user agent: render `false` there and during hydration,
  // the real answer on the client. The user agent never changes, so there is
  // nothing to subscribe to.
  const inAppBrowser = useSyncExternalStore(
    noSubscription,
    () => detectCurrentInAppBrowser().isInApp,
    () => false,
  );
  const currentProgress = uploadProgress || originalUploadProgress || 0;
  // Ensure progress values are between 0 and 100
  const normalizedProgress = Math.min(Math.max(currentProgress, 0), 100);
  const allPhotos = files.reduce((acc, file) => {
    return acc + (file?.count || 1);
  }, 0);
  return (
    <div ref={parentRef}>
      <PhotoUpload onUpload={onUpload} setDisable={setDisableSubmit} />

      {/* Sticky Upload Progress Indicator */}
      {isUploadingAny && (
        <Paper
          elevation={4}
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            // Below the MUI portal layer (theme.zIndex.modal = 1300) so Select
            // menus and dialogs (e.g. the country dropdown in the embedded
            // prefill panel) stack ABOVE this card, not behind it.
            zIndex: (theme) => theme.zIndex.modal - 1,
            p: overlaySlot ? { xs: 1.5, sm: 2 } : 2,
            minWidth: "300px",
            // Widen + allow scrolling when the prefill panel is embedded so the
            // whole "uploading + save time at checkout" card fits on screen.
            //
            // `width` as well as `maxWidth`: without it the card sizes to its
            // content and settled around two thirds of a phone screen, leaving
            // the address fields needlessly cramped inside a wide-open margin.
            width: overlaySlot ? { xs: "96vw", sm: 560 } : undefined,
            maxWidth: overlaySlot ? { xs: "96vw", sm: 560 } : "90vw",
            maxHeight: overlaySlot ? "90vh" : undefined,
            overflowY: overlaySlot ? "auto" : undefined,
            backgroundColor: "primary.main",
            color: "white",
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          }}
        >
          {/*
            * Pinned to the top of the card while the prefill form scrolls
            * beneath it. The card is capped at 90vh and scrolls, so on a phone
            * the progress bar used to scroll out of sight the moment the
            * customer reached down the form — leaving them filling in an
            * address with no idea whether the upload was still going.
            *
            * The negative margins let the bar span the card's padding, so
            * nothing shows through at the edges as content passes underneath.
            */}
          <Box
            sx={
              overlaySlot
                ? {
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: "primary.main",
                    // Must mirror the card's padding above, or content shows
                    // through at the edges as it scrolls underneath.
                    mx: { xs: -1.5, sm: -2 },
                    mt: { xs: -1.5, sm: -2 },
                    px: { xs: 1.5, sm: 2 },
                    pt: { xs: 1.5, sm: 2 },
                    pb: 1,
                  }
                : undefined
            }
          >
            <Typography variant="h6" align="center" gutterBottom>
              {translate("order.uploading")}
            </Typography>
            <Typography variant="body2" align="center" sx={{ mb: 1, opacity: 0.85 }}>
              {translate("order.uploadingHint")}
            </Typography>
            {/*
              * Inside Facebook/Instagram, switching away from the app cuts the
              * upload off (FOTO-NEXTJS-16B: 30 Facebook devices hid the page
              * mid-upload). Not dismissable: it only shows while uploading,
              * which is exactly when it matters.
              */}
            {inAppBrowser && (
              <Typography
                variant="body2"
                align="center"
                role="status"
                sx={{ mb: 1, fontWeight: 700 }}
              >
                {translate("order.stayInAppWhileUploading")}
              </Typography>
            )}
            <LinearProgress
              sx={{
                height: "12px",
                borderRadius: "6px",
                backgroundColor: "rgba(255,255,255,0.3)",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: "white",
                },
              }}
              variant="determinate"
              value={normalizedProgress}
            />
            <Typography variant="body2" align="center" sx={{ mt: 1 }}>
              {Math.round(normalizedProgress)}% {translate("order.complete")}
            </Typography>
          </Box>
          {overlaySlot && <Box sx={{ mt: 2 }}>{overlaySlot}</Box>}
        </Paper>
      )}

      <>
        {files.length > 0 && (
          <PhotoList
            onReorder={onReorder}
            parentRef={parentRef}
            allowEdit={true}
            disable={disableSubmit}
          />
        )}

        <Box sx={{ display: { md: "flex" }, justifyContent: { md: "center" } }}>
          <ActionButton
            onClick={onCreateOrderAndUpload}
            disabled={disableSubmit || files.length === 0}
            sticky={true}
          >
            {translate("order.uploadPhotos") + " (" + allPhotos + ")"}
          </ActionButton>
        </Box>

        {disableSubmit && !isUploadingAny && (
          <Box>
            <CircularProgress />
          </Box>
        )}
      </>

      {(uploadError || originalUploadError) && (
        <Box
          id={UPLOAD_ERROR_ID}
          sx={{
            color: "red",
            mt: 2,
            p: 2,
            backgroundColor: "error.light",
            borderRadius: 1,
          }}
        >
          {localizeServerMessage(
            uploadError?.message || originalUploadError?.message,
            translate,
          )}
        </Box>
      )}
    </div>
  );
};

export default UploadSection;
